import React, { useState } from 'react';
import { Box } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import qs from 'qs';

import { cancelMemberPointGrant, getMemberPoints } from '../../../api/member-point';
import { MEMBER_ADDITION_INFO_KEY } from '../../../constants/queryKeys';
import PointSummaryCards from '../../point/PointSummaryCards';
import PointHistorySection from '../../point/PointHistorySection';
import PointChangeModal from './PointChangeModal';
import PointHistoryEditModal from './PointHistoryEditModal';
import {
  BalanceCard,
  BalanceHeaderRow,
  BalanceLabel,
  BalanceValue,
  ExpiringText,
  ChangeButton,
  SummaryCardsWrapper,
  TopRow,
} from './UserPointHistoryStyled';

// 잔액/합계만 필요하므로 최소 사이즈로 조회 (내역 목록은 PointHistorySection이 별도 조회)
const SUMMARY_QUERY_PARAMS = { type: 'ALL', page: 1, limit: 1 };

const UserPointHistory = ({ user, bigUser }) => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);

  const token = bigUser?.token;
  const memberId = user?.memberId;
  // 다른 탭 컴포넌트(UserCourseHistory 등)와 동일하게 로그인 관리자 소속 지점을 사용
  const branchId = bigUser?.branch?.branchId;

  const isReady = !!memberId && !!branchId && !!token;

  const { data: summary } = useQuery({
    queryKey: ['memberPoints', memberId, branchId, 'summary'],
    queryFn: () => getMemberPoints(memberId, qs.stringify({ branchId, ...SUMMARY_QUERY_PARAMS }), token),
    enabled: isReady,
  });

  const balance = summary?.balance ?? 0;

  const fetchHistories = queryString => getMemberPoints(memberId, `${queryString}&branchId=${branchId}`, token);

  return (
    <>
      <TopRow>
        <BalanceCard>
          <BalanceHeaderRow>
            <BalanceLabel>{t('보유 포인트')}</BalanceLabel>
            <ChangeButton onClick={() => setIsModalOpen(true)}>{t('변경')}</ChangeButton>
          </BalanceHeaderRow>
          <Box sx={{ display: 'flex', alignItems: 'baseline', gap: '12px', flexWrap: 'wrap' }}>
            <BalanceValue>{Number(balance).toLocaleString()} P</BalanceValue>
            {/* 소멸 예정 — 지점 기준 일수 안에 만료되는 잔여 합과 가장 빠른 만료일 (기간 안에 없으면 숨김) */}
            {summary?.expiringDate && (
              <ExpiringText>
                {summary.expiringDays}{t('일 이내 소멸 예정')} {Number(summary.expiringPoint || 0).toLocaleString()} P
              </ExpiringText>
            )}
          </Box>
        </BalanceCard>

        <SummaryCardsWrapper>
          <PointSummaryCards totalEarnPoint={summary?.totalEarnPoint} totalDeductPoint={summary?.totalDeductPoint} />
        </SummaryCardsWrapper>
      </TopRow>

      <PointHistorySection
        fetchHistories={fetchHistories}
        queryKeyPrefix={['memberPoints', memberId, branchId]}
        showSearch={false}
        enabled={isReady}
        excelFilename={`회원포인트내역_${user?.name || memberId || ''}`}
        onCancelGrant={row => cancelMemberPointGrant(memberId, row.pointHistoryId, null, token)}
        onEditHistory={row => setEditRow(row)}
        invalidateKeysAfterChange={[MEMBER_ADDITION_INFO_KEY(memberId, branchId)]}
      />

      <PointHistoryEditModal
        open={!!editRow}
        onClose={() => setEditRow(null)}
        row={editRow}
        memberId={memberId}
        branchId={branchId}
        token={token}
      />

      <PointChangeModal
        open={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        memberId={memberId}
        branchId={branchId}
        token={token}
        balance={balance}
      />
    </>
  );
};

export default UserPointHistory;
